import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '../api/client';

interface AsyncBoundaryProps {
  loading: boolean;
  error: ApiError | undefined;
  onRetry?: () => void;
  children: ReactNode;
}

/**
 * The loading and error branches every fetching view needs, in one place so the
 * pages differ only in what they render on success.
 *
 * Children are rendered only once loading has finished without error, which lets
 * a caller narrow `data` to non-undefined at the point of use.
 */
export function AsyncBoundary({
  loading,
  error,
  onRetry,
  children,
}: AsyncBoundaryProps): ReactElement {
  const { t } = useTranslation();

  if (loading) {
    return (
      <p className="text-fog-500" role="status" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  if (error !== undefined) {
    return (
      <div className="space-y-3" role="alert">
        <p className="text-fog-500">{t('common.error')}</p>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm font-medium underline underline-offset-4"
          >
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
